import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import api from '../api/axios';
import { toast } from 'sonner';
import { Plus, Calendar, TrendingUp, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export const Roadmap = () => {
  const { activeProject } = useAuth();
  const { t } = useLanguage();
  
  // Status columns with translations
  const STATUS_COLUMNS = [
    { id: 'backlog', label: t.backlog, color: 'bg-gray-100 dark:bg-gray-800', textColor: 'text-gray-900 dark:text-gray-100', subColor: 'text-gray-600 dark:text-gray-400' },
    { id: 'planned', label: t.planned, color: 'bg-blue-100 dark:bg-blue-900', textColor: 'text-blue-900 dark:text-blue-100', subColor: 'text-blue-600 dark:text-blue-400' },
    { id: 'in_development', label: t.inDevelopment, color: 'bg-purple-100 dark:bg-purple-900', textColor: 'text-purple-900 dark:text-purple-100', subColor: 'text-purple-600 dark:text-purple-400' },
    { id: 'testing', label: t.testing, color: 'bg-yellow-100 dark:bg-yellow-900', textColor: 'text-yellow-900 dark:text-yellow-100', subColor: 'text-yellow-600 dark:text-yellow-400' },
    { id: 'completed', label: t.completed, color: 'bg-green-100 dark:bg-green-900', textColor: 'text-green-900 dark:text-green-100', subColor: 'text-green-600 dark:text-green-400' },
  ];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [roadmapToDelete, setRoadmapToDelete] = useState(null);
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    status: 'backlog',
    quarter: ''
  });

  useEffect(() => {
    if (activeProject) {
      fetchRoadmap();
    }
  }, [activeProject]);

  const fetchRoadmap = async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const response = await api.get(`/roadmap?project_id=${activeProject.id}`);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch roadmap:', error);
      toast.error(t.roadmapLoadFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newItem };
      if (activeProject?.id) payload.project_id = activeProject.id;
      await api.post('/roadmap', payload);
      toast.success(t.roadmapItemCreated);
      setShowDialog(false);
      setNewItem({ title: '', description: '', status: 'backlog', quarter: '' });
      fetchRoadmap();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.roadmapItemCreateFailed);
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      await api.put(`/roadmap/${itemId}`, { status: newStatus });
      toast.success(t.statusUpdated);
      // Use setTimeout to allow Select portal to cleanup before state update
      setTimeout(() => {
        fetchRoadmap();
      }, 150);
    } catch (error) {
      console.error('Failed to update status:', error);
      const errorMessage = error.response?.data?.detail || t.statusUpdateFailed;
      toast.error(errorMessage);
      fetchRoadmap();
    }
  };

  const getItemsByStatus = (status) => {
    return items.filter(item => item.status === status);
  };

  const handleDeleteClick = (item) => {
    if (item.feedback_id) {
      toast.warning(t.roadmapItemFromFeedbackCannotDelete);
      return;
    }
    setRoadmapToDelete(item);
  };

  const handleDeleteConfirm = async () => {
    if (!roadmapToDelete) return;
    try {
      await api.delete(`/roadmap/${roadmapToDelete.id}`);
      toast.success(t.roadmapItemDeleted);
      setRoadmapToDelete(null);
      fetchRoadmap();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.roadmapItemDeleteFailed);
      setRoadmapToDelete(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Product Roadmap - FeedPanels</title>
        <meta name="description" content="Build and share your product roadmap in FeedPanels. Plan features, track in-development items, and publish completed updates for customers." />
        <link rel="canonical" href="https://feedpanels.com/dashboard/roadmap" />
      </Helmet>
      <>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-4 md:gap-0 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2" data-testid="roadmap-page-title">
              {t.productRoadmap}
            </h1>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">{t.roadmapDescription}</p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="w-full md:w-auto text-xs md:text-sm" data-testid="new-roadmap-item-button">
                <Plus className="w-3 md:w-4 h-3 md:h-4 mr-2" />
                {t.newItem}
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full max-w-md">
              <DialogHeader>
                <DialogTitle className="text-lg md:text-xl dark:text-white">{t.createRoadmapItem}</DialogTitle>
                <DialogDescription className="text-xs md:text-sm dark:text-gray-400">
                  {t.createRoadmapDescription}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-3 md:space-y-4">
                <div>
                  <label className="text-xs md:text-sm font-medium dark:text-gray-300">{t.roadmapTitle}</label>
                  <Input
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder={t.roadmapTitlePlaceholder}
                    required
                    data-testid="roadmap-title-input"
                    className="text-xs md:text-sm mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs md:text-sm font-medium dark:text-gray-300">{t.roadmapDescriptionLabel}</label>
                  <Textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder={t.roadmapDescriptionPlaceholder}
                    required
                    rows={4}
                    data-testid="roadmap-description-input"
                    className="text-xs md:text-sm mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs md:text-sm font-medium dark:text-gray-300">{t.quarter}</label>
                  <Input
                    value={newItem.quarter}
                    onChange={(e) => setNewItem({ ...newItem, quarter: e.target.value })}
                    placeholder={t.quarterPlaceholder}
                    data-testid="roadmap-quarter-input"
                    className="text-xs md:text-sm mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs md:text-sm font-medium dark:text-gray-300">{t.status}</label>
                  <Select value={newItem.status} onValueChange={(value) => setNewItem({ ...newItem, status: value })}>
                    <SelectTrigger data-testid="roadmap-status-select" className="text-xs md:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_COLUMNS.map((col) => (
                        <SelectItem key={col.id} value={col.id} className="text-xs md:text-sm">
                          {col.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full text-xs md:text-sm" data-testid="roadmap-submit-button">
                  {t.createItem}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-600 dark:text-gray-400">{t.loadingRoadmap}</div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400">{t.noRoadmapItems}</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-6 overflow-x-auto pb-4">
            {STATUS_COLUMNS.map((column) => (
              <div key={column.id} className="space-y-2 md:space-y-4 flex-shrink-0 w-full sm:w-auto">
                <div className={`${column.color} rounded-lg md:rounded-xl p-3 md:p-4`}>
                  <h3 className={`font-bold text-base md:text-lg ${column.textColor}`} data-testid={`column-${column.id}`}>
                    {column.label}
                  </h3>
                  <p className={`text-xs md:text-sm ${column.subColor} mt-1`}>
                    {getItemsByStatus(column.id).length} {t.items}
                  </p>
                </div>
                <div className="space-y-2 md:space-y-3">
                  {getItemsByStatus(column.id).map((item) => (
                    <Card key={item.id} data-testid={`roadmap-item-${item.id}`}>
                      <CardContent className="p-2 md:p-4">
                        {item.feedback_id && (
                          <Badge variant="outline" className="text-[10px] md:text-xs mb-2 bg-cyan-50 dark:bg-cyan-900 text-cyan-700 dark:text-cyan-200 border-cyan-200 dark:border-cyan-800">
                            {t.fromFeedback}
                          </Badge>
                        )}
                        <h4 className="font-semibold text-xs md:text-sm text-gray-900 dark:text-white mb-2 line-clamp-2">{item.title}</h4>
                        <p className="text-[11px] md:text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{item.description}</p>
                        <div className="flex items-center justify-between text-[10px] md:text-xs gap-2">
                          {item.quarter && (
                            <Badge variant="outline" className="flex-shrink-0 dark:border-gray-600 dark:text-gray-300">
                              <Calendar className="w-2 md:w-3 h-2 md:h-3 mr-0.5 md:mr-1" />
                              {item.quarter}
                            </Badge>
                          )}
                          {item.votes > 0 && (
                            <div className="flex items-center text-gray-500 dark:text-gray-400 flex-shrink-0">
                              <TrendingUp className="w-2 md:w-3 h-2 md:h-3 mr-0.5 md:mr-1" />
                              {item.votes} {t.votes}
                            </div>
                          )}
                        </div>
                        <Select
                          key={`${item.id}-${item.status}`}
                          value={item.status}
                          onValueChange={(value) => handleStatusChange(item.id, value)}
                        >
                          <SelectTrigger className="w-full mt-2 h-6 md:h-7 text-[10px] md:text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_COLUMNS.map((col) => (
                              <SelectItem key={col.id} value={col.id} className="text-[10px] md:text-xs">
                                {col.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="mt-2 h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                          onClick={() => handleDeleteClick(item)}
                          title={t.deleteRoadmapItem}
                          data-testid={`roadmap-delete-${item.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!roadmapToDelete} onOpenChange={(open) => !open && setRoadmapToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">{t.deleteRoadmapItem}</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-400">
              {t.deleteRoadmapItemConfirm}
              {roadmapToDelete && <strong className="block mt-1">"{roadmapToDelete.title}"</strong>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRoadmapToDelete(null)}>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
    </>
  );
};